import React from 'react'
import { isReactNativeApp, sendToNative } from '@mobi/web-native-comms/web'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { AppRoutes } from '@core/AppRoutes'
import { generateShortenedUrl } from '@core/Areas/ShortenedUrls/api'
import { doesClientSupportShare } from '@core/Areas/ShareBet/helpers/doesClientSupportShare'
import type { FollowMeProfile } from '@core/Areas/FollowMe/types'
import { createImageBase64FromHtml } from '@core/Areas/ShareBet/ShareBetOutgoing/Components/ShareWithExternal/helpers/createImageFromHtml'
import { trackBetLoopShareProfile } from '@core/Areas/FollowMe/helpers/analytics'
import { ShareProfileContent } from './Components/ShareProfileContent'

const ShareCopy = {
  TitleMyProfile: 'Check out my profile on Bet Loop!',
  TitleOtherProfile: 'Check out this profile on Bet Loop!',
  MessageMyProfile: 'Stay in the Loop and follow my bets on TABtouch. Check out my profile 👉 ',
  MessageOtherProfile: (username: FollowMeProfile['displayName']) =>
    `Stay in the Loop and follow ${username} on TABtouch. Check out their profile 👉 `,
} as const

export const useShareProfile = (profile: FollowMeProfile, isMyProfile: boolean) => {
  const shareElRef = React.useRef<HTMLDivElement>(null)

  const [canShare] = React.useState(doesClientSupportShare)
  const [shouldRenderNativeShareEl, setShouldRenderNativeShareEl] = React.useState(false)
  const [isPreparingNativeShare, setIsPreparingNativeShare] = React.useState(false)
  const [isPreparingWebShare, setIsPreparingWebShare] = React.useState(false)

  // Handle Native Share Programmatically
  React.useEffect(() => {
    if (!shouldRenderNativeShareEl || !isPreparingNativeShare) return

    shareWithNative({ profile, isMyProfile, previewEl: shareElRef.current }).finally(() => {
      setIsPreparingNativeShare(false)
    })
  }, [profile, isMyProfile, shouldRenderNativeShareEl, isPreparingNativeShare])

  const handleShareProfileClick = () => {
    trackBetLoopShareProfile(isMyProfile)

    if (isReactNativeApp) {
      setShouldRenderNativeShareEl(true)
      setIsPreparingNativeShare(true)
      return
    } else {
      setIsPreparingWebShare(true)
      shareWithBrowser(profile, isMyProfile)
        .finally(() => setIsPreparingWebShare(false))
        .catch(err => {
          const errorText = (err as Error).toString?.().toLowerCase()
          if (errorText && (errorText.includes('cancel') || errorText.includes('abort'))) return
          displayErrorToast()
        })
    }
  }

  return {
    canShare,
    handleShareProfileClick,
    isLoadingShare: isPreparingWebShare || isPreparingNativeShare,
    /** ONLY renders on initiating native share (outside of viewport) */
    NativeShareProfileContent: () =>
      shouldRenderNativeShareEl ? <ShareProfileContent profile={profile} ref={shareElRef} /> : null,
  }
}

// =============
// Local Helpers
// =============

async function shareWithNative({
  profile,
  isMyProfile,
  previewEl,
}: {
  profile: FollowMeProfile
  isMyProfile: boolean
  previewEl: HTMLDivElement | null
}) {
  if (!previewEl) throw Error('Unable to create share')
  const [base64Image, shareUrl] = await Promise.all([
    await createImageBase64FromHtml(previewEl),
    await buildShareUrl(profile.id),
  ])
  const title = isMyProfile ? ShareCopy.TitleMyProfile : ShareCopy.TitleOtherProfile
  sendToNative('SHARE_SHEET_REQUEST', {
    url: base64Image,
    title,
    subject: title,
    message: buildShareMessage({ profile, isMyProfile, shareUrl }),
  })
}

async function shareWithBrowser(profile: FollowMeProfile, isMyProfile: boolean) {
  const shareUrl = await buildShareUrl(profile.id)
  const navigatorShareData: ShareData = {
    title: isMyProfile ? ShareCopy.TitleMyProfile : ShareCopy.TitleOtherProfile,
    text: buildShareMessage({ profile, isMyProfile, shareUrl }),
  }
  if (navigator.canShare?.(navigatorShareData)) {
    return await navigator.share(navigatorShareData)
  }
  throw Error('No capability to share')
}

const buildShareUrl = async (profileId: FollowMeProfile['id']): Promise<string> => {
  const rawLink = `${window.location.origin}${AppRoutes.BetLoopProfile}/${profileId}`
  const shortenedUrl = await generateShortenedUrl(new URL(rawLink))
  return shortenedUrl.toString()
}

const displayErrorToast = () =>
  addToast({
    id: 'Share-Profile-Error',
    timeoutMs: Infinity,
    title: 'Unable to Share Profile',
    icon: 'error',
    theme: 'dark',
  })

const buildShareMessage = ({
  profile,
  isMyProfile,
  shareUrl,
}: {
  profile: FollowMeProfile
  isMyProfile: boolean
  shareUrl: string
}) =>
  isMyProfile
    ? ShareCopy.MessageMyProfile + shareUrl
    : ShareCopy.MessageOtherProfile(profile.displayName) + shareUrl
